/**
 * « Audios animations » (permission `speaker`, migrations 0678-0679) : les
 * textes des animations SKILLCARDS qui n'ont pas encore de voix, par matière,
 * niveau et animation. On ouvre une animation, on lit le texte au prompteur,
 * on s'enregistre (« Annuler la prise » la jette) ; la prise est analysée en
 * arrière-plan pendant qu'on enregistre le texte suivant, puis on la garde, la
 * refait ou la jette, dans l'ordre qu'on veut (`voice-takes.tsx`, comme le
 * Studio). « Tester l'animation » joue
 * le brouillon, voix déjà gardées comprises.
 */

import { useMemo, useState } from 'react';
import { Check, ChevronDown, Clapperboard, Mic, Play, X } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import {
  JRAF_PALETTE,
  validateJraf,
  type JrafDocument,
  type VoiceSlot,
} from '@jeraisonne/animation';
import { Button, cn } from '@jeraisonne/ui';
import { AnimationPlayer } from '@jeraisonne/ui/components/animations/animation-player';
import { RecordingBar, TakeCard, useVoiceTakes } from '@/features/animations/voice-takes';
import { useAnimationSoundtrack } from '@/lib/animations/audio';
import { useSpeakerAvatars } from '@/lib/animations/speakers';
import { isAdmin, useAuth } from '@/lib/auth';
import { levelLabel, subjectLabel } from '@jeraisonne/db/curriculum';
import { animationAssetUrl } from '@/lib/animations/voice';
import {
  groupSpeakerTasks,
  useCanRecordVoices,
  useKeepSpeakerVoice,
  speakerRoleOptions,
  speakerRoleTitle,
  useSpeakerAnimation,
  useAnimationsReadyToShare,
  useSpeakerTasks,
  videoStatusLabel,
  type ReadyToShare,
  type SpeakerSlot,
  type SpeakerTask,
} from '@/lib/animations/speaker';

const HORA_BG: Record<string, string> = {
  H: JRAF_PALETTE['hora-h'],
  O: JRAF_PALETTE['hora-o'],
  R: JRAF_PALETTE['hora-r'],
  A: JRAF_PALETTE['hora-a'],
};

/** « 3 textes » */
export function textsLabel(n: number): string {
  return `${n} texte${n > 1 ? 's' : ''}`;
}

export function SpeakerVoicesPage() {
  const allowed = useCanRecordVoices();
  const tasks = useSpeakerTasks(allowed);
  const groups = useMemo(() => groupSpeakerTasks(tasks.data ?? []), [tasks.data]);
  const missing = (tasks.data ?? []).reduce((n, t) => n + t.missing, 0);
  const { profile, user } = useAuth();
  const admin = isAdmin(profile, user?.email);

  return (
    <div className="native:pt-[calc(env(safe-area-inset-top)+1.5rem)] mx-auto max-w-3xl px-4 py-6 sm:py-8">
      <h1 className="text-ink-900 text-2xl font-bold">Audios animations</h1>
      <p className="text-ink-600 mt-1 text-sm">
        Prête ta voix aux animations des SKILLCARDS : ouvre une animation, lis le texte, garde ta
        prise. Elle sera entendue par les élèves quand l’animation sera republiée.
      </p>

      {!allowed && (
        <p className="border-ink-200 text-ink-700 mt-6 rounded-2xl border bg-white p-4 text-sm">
          Cette rubrique est réservée aux comptes à qui l’équipe JeRaisonne a confié les voix des
          animations.
        </p>
      )}

      {admin && <ReadyToShareSection />}

      {allowed && tasks.isLoading && (
        <p role="status" className="text-ink-600 mt-6 text-sm">
          Chargement des textes…
        </p>
      )}
      {allowed && tasks.error && (
        <p role="alert" className="text-error-600 mt-6 text-sm">
          {tasks.error.message}
        </p>
      )}
      {allowed && tasks.data && (
        <p className="text-ink-800 mt-4 text-sm font-semibold" data-testid="speaker-total">
          {missing === 0
            ? 'Toutes les animations ont leur voix. Merci !'
            : `${textsLabel(missing)} à enregistrer dans ${tasks.data.length} animation${tasks.data.length > 1 ? 's' : ''}`}
        </p>
      )}

      <div className="mt-4 space-y-6">
        {groups.map((g) => (
          <section key={g.subject ?? '-'} aria-label={g.label}>
            <h2 className="text-ink-900 text-lg font-bold">
              {g.label}{' '}
              <span className="text-ink-500 text-sm font-medium">· {textsLabel(g.missing)}</span>
            </h2>
            <div className="mt-2 space-y-3">
              {g.levels.map((l) => (
                <details
                  key={l.level ?? '-'}
                  className="border-ink-200 rounded-2xl border bg-white"
                  open={g.levels.length === 1}
                >
                  <summary className="text-ink-900 flex min-h-11 cursor-pointer items-center justify-between gap-2 px-4 py-2 text-sm font-semibold">
                    <span>{l.label}</span>
                    <span className="text-ink-500 text-[13px] font-medium">
                      {l.animations.length} animation{l.animations.length > 1 ? 's' : ''} ·{' '}
                      {textsLabel(l.missing)}
                    </span>
                  </summary>
                  <ul className="divide-ink-100 border-ink-100 divide-y border-t">
                    {l.animations.map((a) => (
                      <AnimationRow key={a.id} task={a} />
                    ))}
                  </ul>
                </details>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

/**
 * Admin : animations complètes (toutes les voix) pas encore diffusées sur les
 * réseaux — les tester, puis les exporter et les publier (partie « Vidéo »).
 */
function ReadyToShareSection() {
  const q = useAnimationsReadyToShare(true);
  if (q.isLoading || !q.data) return null;
  return (
    <section aria-label="Prêtes à diffuser" className="mt-6">
      <h2 className="text-ink-900 flex items-center gap-2 text-lg font-bold">
        <Clapperboard className="text-primary-600 h-5 w-5" aria-hidden />
        Prêtes à diffuser{' '}
        <span className="text-ink-500 text-sm font-medium">· {q.data.length}</span>
      </h2>
      <p className="text-ink-600 mt-1 text-[13px]">
        Toutes les voix sont enregistrées, pas encore publiées sur les réseaux. Teste-les, puis
        exporte la vidéo et publie-la.
      </p>
      {q.data.length === 0 ? (
        <p className="text-ink-600 mt-2 text-sm">Aucune animation en attente de diffusion.</p>
      ) : (
        <ul className="divide-ink-100 border-ink-200 mt-2 divide-y rounded-2xl border bg-white">
          {q.data.map((a) => (
            <ReadyRow key={a.id} item={a} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ReadyRow({ item }: { item: ReadyToShare }) {
  const [open, setOpen] = useState(false);
  const where = [
    item.subject ? subjectLabel(item.subject) : null,
    item.level ? levelLabel(item.level) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1">
          <span className="text-ink-900 block text-sm font-semibold">{item.title}</span>
          {where && <span className="text-ink-500 block text-[12px]">{where}</span>}
        </span>
        {item.needs_republish && (
          <span className="bg-warning-50 text-ink-800 rounded-full px-2.5 py-0.5 text-[12px] font-semibold">
            À republier
          </span>
        )}
        <span className="bg-ink-100 text-ink-700 rounded-full px-2.5 py-0.5 text-[12px] font-semibold">
          {videoStatusLabel(item.video_status)}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          <Play className="h-4 w-4" aria-hidden /> {open ? 'Fermer' : 'Tester'}
        </Button>
        <Link
          to="/tutor/admin/animations/$animationId"
          params={{ animationId: item.id }}
          hash="video"
          className="bg-primary-500 inline-flex min-h-9 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold text-white active:scale-[0.98]"
        >
          <Clapperboard className="h-4 w-4" aria-hidden /> Republier, exporter et publier
        </Link>
      </div>
      {open && <AnimationTexts animationId={item.id} />}
    </li>
  );
}

function AnimationRow({ task }: { task: SpeakerTask }) {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="hover:bg-ink-50 flex min-h-11 w-full items-center gap-3 px-4 py-2.5 text-left"
      >
        <span className="text-ink-900 min-w-0 flex-1 text-sm font-medium">{task.title}</span>
        <span className="bg-ink-100 text-ink-700 shrink-0 rounded-full px-2.5 py-0.5 text-[12px] font-semibold">
          {task.missing} / {task.total}
        </span>
        <ChevronDown
          className={cn('text-ink-500 h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      {open && <AnimationTexts animationId={task.id} />}
    </li>
  );
}

/** Textes encore sans voix d'une animation, chacun avec son enregistreur. */
export function AnimationTexts({ animationId }: { animationId: string }) {
  const q = useSpeakerAnimation(animationId);
  const keep = useKeepSpeakerVoice();
  const [recording, setRecording] = useState<string | null>(null);
  const takes = useVoiceTakes(animationId);
  const [kept, setKept] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  /** dialogue : le rôle que je tiens (null : tous les rôles) */
  const [myRole, setMyRole] = useState<string | null>(null);

  if (q.isLoading)
    return (
      <p role="status" className="text-ink-600 px-4 pb-4 text-sm">
        Chargement…
      </p>
    );
  if (q.error || !q.data)
    return (
      <p role="alert" className="text-error-600 px-4 pb-4 text-sm">
        {q.error?.message ?? 'Animation introuvable.'}
      </p>
    );

  const roles = speakerRoleOptions(q.data.slots);
  const dialogue = roles.length > 1;
  const role = dialogue ? myRole : null;
  const todo = q.data.slots.filter((s) => !s.recorded && (!role || s.role === role));
  const done = q.data.slots.filter((s) => s.recorded).length;
  const mineCount = q.data.slots.filter((s) => s.mine).length;
  const others = [
    ...new Set(
      q.data.slots.filter((s) => s.recorded && !s.mine && s.recordedBy).map((s) => s.recordedBy!),
    ),
  ];

  return (
    <div className="space-y-3 px-4 pb-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setTesting(true)}>
          <Play className="h-4 w-4" aria-hidden /> Tester l’animation
        </Button>
        <span className="text-ink-500 text-[12px]">
          {done} / {q.data.slots.length} avec une voix enregistrée
        </span>
      </div>
      {(mineCount > 0 || others.length > 0) && (
        <p className="text-ink-600 text-[12px]" data-voice-credits="">
          {mineCount > 0 ? `${mineCount} enregistré${mineCount > 1 ? 's' : ''} par toi` : ''}
          {mineCount > 0 && others.length > 0 ? ' · ' : ''}
          {others.length > 0 ? `aussi : ${others.join(', ')}` : ''}
        </p>
      )}
      {dialogue && (
        <div className="space-y-1">
          <p className="text-ink-600 text-xs font-semibold">
            Dialogue : quel rôle enregistres-tu ?
          </p>
          <div role="radiogroup" aria-label="Rôle à enregistrer" className="flex flex-wrap gap-2">
            {[{ role: null, label: 'Tous les rôles', todo: null }, ...roles].map((o) => (
              <button
                key={o.role ?? 'all'}
                type="button"
                role="radio"
                aria-checked={role === o.role}
                onClick={() => setMyRole(o.role)}
                className={cn(
                  'min-h-[2.75rem] rounded-full border px-3 text-sm active:scale-[0.98]',
                  role === o.role
                    ? 'border-primary-500 bg-primary-500 font-semibold text-white'
                    : 'border-ink-200 text-ink-700 bg-white',
                )}
              >
                {o.role ? speakerRoleTitle(o.role, o.label) : o.label}
                {o.todo != null && o.todo > 0 ? ` (${o.todo})` : ''}
              </button>
            ))}
          </div>
        </div>
      )}
      {kept && (
        <p role="status" className="text-success-600 text-[13px] font-semibold">
          <Check className="mr-1 inline h-4 w-4" aria-hidden />
          Prise gardée : « {kept} ».
        </p>
      )}
      {keep.error && (
        <p role="alert" className="text-error-600 text-[13px]">
          {keep.error.message}
        </p>
      )}
      {todo.length === 0 ? (
        <p className="text-ink-700 text-sm">
          {role
            ? 'Toutes les répliques de ce rôle ont leur voix.'
            : 'Tous les textes de cette animation ont leur voix.'}
        </p>
      ) : (
        <ul className="space-y-3">
          {todo.map((slot) => (
            <li
              key={slot.key}
              className="border-ink-200 rounded-2xl border p-3"
              data-slot={slot.key}
            >
              <p className="text-ink-900 flex flex-wrap items-center gap-1.5 text-sm font-semibold">
                {slot.hora && (
                  <span
                    className="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold text-white"
                    style={{ backgroundColor: HORA_BG[slot.hora] }}
                    aria-hidden
                  >
                    {slot.hora}
                  </span>
                )}
                {slot.label}
                {dialogue && (
                  <span
                    className="bg-primary-50 text-primary-700 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    data-slot-role={slot.role}
                  >
                    {speakerRoleTitle(slot.role, slot.roleLabel)}
                  </span>
                )}
                {slot.videoOnly && (
                  <span className="bg-ink-100 text-ink-600 rounded-full px-2 py-0.5 text-[11px] font-semibold">
                    vidéo
                  </span>
                )}
              </p>
              {recording === slot.key ? (
                <RecordingBar
                  slot={asVoiceSlot(slot)}
                  onClose={() => setRecording(null)}
                  onDone={(blob, name) => {
                    // envoyée en arrière-plan : on peut enregistrer le texte suivant
                    takes.submit(asVoiceSlot(slot), blob, name);
                    setRecording(null);
                  }}
                />
              ) : (
                <>
                  <p className="text-ink-700 mt-1 text-[15px] leading-snug">{slot.say}</p>
                  {takes.takes[slot.key] ? (
                    <TakeCard
                      state={takes.takes[slot.key]!}
                      saving={keep.isPending && keep.variables?.slotKey === slot.key}
                      onRedo={() => {
                        setKept(null);
                        setRecording(slot.key);
                      }}
                      onDiscard={() => takes.discard(slot.key)}
                      onForce={() => takes.force(slot.key)}
                      onKeep={(take) =>
                        keep.mutate(
                          {
                            animationId,
                            slotKey: slot.key,
                            say: slot.say,
                            path: take.path,
                            durationMs: take.durationMs,
                            words: take.words,
                          },
                          {
                            onSuccess: () => {
                              takes.discard(slot.key);
                              setKept(slot.label);
                            },
                          },
                        )
                      }
                    />
                  ) : (
                    <Button
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        keep.reset();
                        setKept(null);
                        setRecording(slot.key);
                      }}
                    >
                      <Mic className="h-4 w-4" aria-hidden /> Enregistrer
                    </Button>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {testing && (
        <DraftPlayer doc={q.data.doc} title={q.data.title} onClose={() => setTesting(false)} />
      )}
    </div>
  );
}

/** Le recorder du Studio ne lit que le texte et le titre de la cible. */
function asVoiceSlot(s: SpeakerSlot): VoiceSlot {
  return {
    target: { kind: 'intro' },
    key: s.key,
    label: s.label,
    hora: s.hora as VoiceSlot['hora'],
    text: s.text,
    say: s.say,
    audioAssetId: null,
    videoOnly: s.videoOnly,
    interactive: s.interactive,
    role: s.role as VoiceSlot['role'],
    roleLabel: s.roleLabel,
  };
}

/** Le brouillon joué en plein écran (voix déjà gardées comprises). */
function DraftPlayer({
  doc,
  title,
  onClose,
}: {
  doc: Record<string, unknown>;
  title: string;
  onClose: () => void;
}) {
  const read = useMemo(() => validateJraf(doc), [doc]);
  const jraf: JrafDocument | null = read.ok ? read.doc : null;
  const soundtrack = useAnimationSoundtrack();
  const speakerAvatars = useSpeakerAvatars(jraf);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Tester : ${title}`}
      className="native:pt-[env(safe-area-inset-top)] fixed inset-0 z-[80] flex flex-col bg-[#061433]"
    >
      <div className="flex items-center justify-between gap-2 px-4 py-2">
        <p className="min-w-0 truncate text-sm font-semibold text-white">{title}</p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="flex h-11 w-11 items-center justify-center rounded-full text-white hover:bg-[rgba(255,255,255,0.1)]"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
        {jraf ? (
          <div className="mx-auto max-w-3xl">
            <AnimationPlayer
              doc={jraf}
              locked={[]}
              soundtrack={soundtrack}
              speakerAvatars={speakerAvatars}
              assetUrl={animationAssetUrl}
            />
          </div>
        ) : (
          <p className="p-4 text-sm text-white">
            Cette animation ne peut pas être jouée pour le moment.
          </p>
        )}
      </div>
    </div>
  );
}
